import { Capacitor } from '@capacitor/core';

/**
 * Get the current platform
 * @returns 'ios' | 'android' | 'web'
 */
export const getPlatform = (): string => {
    return Capacitor.getPlatform();
};

/**
 * Check if the app is running on a specific platform
 * Replacement for Ionic's isPlatform()
 *
 * Supported platforms:
 * - 'ios': iOS native app
 * - 'android': Android native app
 * - 'web': Web browser
 * - 'hybrid': iOS or Android native app
 * - 'mobile': iOS or Android (native or web)
 * - 'mobileweb': Mobile web browser
 * - 'desktop': Desktop web browser
 * - 'tablet': Tablet device
 * - 'pwa': Progressive Web App
 *
 * @param platform - The platform to check
 * @returns true if running on the specified platform
 */
export const isPlatform = (platform: string): boolean => {
    const currentPlatform = Capacitor.getPlatform();
    const isNative = Capacitor.isNativePlatform();

    switch (platform.toLowerCase()) {
        case 'ios':
            return currentPlatform === 'ios';

        case 'android':
            return currentPlatform === 'android';

        case 'web':
            return currentPlatform === 'web';

        case 'hybrid':
            // Native app (iOS or Android)
            return isNative;

        case 'mobile':
            // Mobile device (native or web)
            return currentPlatform === 'ios' || currentPlatform === 'android' || isMobileWeb();

        case 'mobileweb':
            // Mobile web browser
            return currentPlatform === 'web' && isMobileWeb();

        case 'desktop':
            // Desktop web browser
            return currentPlatform === 'web' && !isMobileWeb();

        case 'tablet':
            // Tablet device (basic detection)
            return isTablet();

        case 'pwa':
            // Progressive Web App
            return isPWA();

        default:
            console.warn(`Unknown platform: ${platform}`);
            return false;
    }
};

/**
 * Check if running on a mobile web browser
 */
const isMobileWeb = (): boolean => {
    if (typeof window === 'undefined') return false;

    const userAgent = window.navigator.userAgent.toLowerCase();
    return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
};

/**
 * Check if running on a tablet
 */
const isTablet = (): boolean => {
    if (typeof window === 'undefined') return false;

    const userAgent = window.navigator.userAgent.toLowerCase();
    const isTabletUA = /ipad|android(?!.*mobile)|tablet/i.test(userAgent);

    // Additional check for screen size
    const hasTabletScreen = window.innerWidth >= 768 && window.innerWidth <= 1024;

    return isTabletUA || (isMobileWeb() && hasTabletScreen);
};

/**
 * Check if running as a PWA
 */
const isPWA = (): boolean => {
    if (typeof window === 'undefined') return false;

    return (
        window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone === true
    );
};

/**
 * Get detailed platform information
 */
export const getPlatformInfo = () => {
    return {
        platform: getPlatform(),
        isNative: Capacitor.isNativePlatform(),
        isIOS: isPlatform('ios'),
        isAndroid: isPlatform('android'),
        isWeb: isPlatform('web'),
        isHybrid: isPlatform('hybrid'),
        isMobile: isPlatform('mobile'),
        isMobileWeb: isPlatform('mobileweb'),
        isDesktop: isPlatform('desktop'),
        isTablet: isPlatform('tablet'),
        isPWA: isPlatform('pwa')
    };
};
